import QRCode from 'qrcode';

export interface QRGenerationOptions {
  size?: number;
  margin?: number;
  color?: {
    dark?: string;
    light?: string;
  };
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  logo?: {
    enabled?: boolean;
    url?: string;
    size?: number;
    borderRadius?: number;
    backgroundColor?: string;
    uploadedLogoId?: string;
    uploadedLogoName?: string;
  };
  style?: {
    transparentBackground?: boolean;
    dotStyle?: 'square' | 'rounded' | 'dots';
    gradientColors?: {
      enabled: boolean;
      startColor?: string;
      endColor?: string;
      direction?: 'horizontal' | 'vertical' | 'diagonal';
    };
  };
}

// Firebase Cloud Function URL - uses environment variables for security
const getFirebaseFunctionURL = () => {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;

  // If we have a project ID, prefer the clean "Magic Link" URL (Firebase Hosting)
  // This ensures the QR code always looks clean (e.g. https://project.web.app/qr/...)
  // NOTE: This requires the rewrite rule in firebase.json to be deployed to work!
  if (projectId) {
    // Check if we have a custom domain explicitly set, otherwise use standard firebase hosting
    const domain = process.env.REACT_APP_CUSTOM_DOMAIN || 'redirectqr.de';
    return `https://${domain}/qr`;
  }

  // Fallback only if we can't determine the project ID
  return 'https://us-central1-dyna-af724.cloudfunctions.net/redirectQR';
};

export const FIREBASE_FUNCTION_URL = getFirebaseFunctionURL();

export const generateQRCodeURL = (qrId: string): string => {
  return `${FIREBASE_FUNCTION_URL}/${qrId}`;
};

export const generateQRCodeDataURL = async (
  qrId: string,
  options: QRGenerationOptions = {}
): Promise<string> => {
  const url = generateQRCodeURL(qrId);

  // Use higher error correction for logo support
  const errorLevel = options.logo?.enabled ? 'H' : (options.errorCorrectionLevel || 'M');

  const qrOptions = {
    width: options.size || 256,
    margin: options.margin || 2,
    color: {
      dark: options.color?.dark || '#000000',
      light: options.style?.transparentBackground ? '#FFFFFF00' : (options.color?.light || '#FFFFFF'),
    },
    errorCorrectionLevel: errorLevel as any,
  };

  try {
    let dataURL = await QRCode.toDataURL(url, qrOptions);

    // Add logo overlay if enabled
    if (options.logo?.enabled && options.logo?.url) {
      dataURL = await addLogoToQRCode(dataURL, options);
    }

    return dataURL;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

const triggerDownload = (href: string, filename: string) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadQRCodePNG = async (
  qrId: string,
  filename?: string,
  options: QRGenerationOptions = {}
): Promise<void> => {
  try {
    const dataURL = await generateQRCodeDataURL(qrId, options);
    triggerDownload(dataURL, filename || `qr-code-${qrId}.png`);
  } catch (error) {
    console.error('Error downloading QR code as PNG:', error);
    throw new Error('Failed to download QR code as PNG');
  }
};

// Backwards-compatible alias for existing callers.
export const downloadQRCode = downloadQRCodePNG;

// Logo overlay function
const addLogoToQRCode = async (
  qrDataURL: string,
  options: QRGenerationOptions
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    // Enable high-quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const qrImage = new Image();
    qrImage.onload = () => {
      const size = options.size || 256;
      canvas.width = size;
      canvas.height = size;

      // Draw QR code
      ctx.drawImage(qrImage, 0, 0, size, size);

      // Add logo if provided
      if (options.logo?.enabled && options.logo?.url) {
        const logoImage = new Image();

        // Configure for Firebase Storage CORS
        logoImage.crossOrigin = 'anonymous';

        // Add timestamp to prevent caching issues
        const logoUrl = options.logo.url + (options.logo.url.includes('?') ? '&' : '?') + '_t=' + Date.now();

        // Set up retry mechanism for failed loads
        let retryCount = 0;
        const maxRetries = 2;
        logoImage.onload = () => {
          // Convert percentage to pixels (e.g., 15 becomes 0.15)
          const logoPercentage = (options.logo?.size || 15) / 100;
          const logoSize = size * logoPercentage;
          const logoX = (size - logoSize) / 2;
          const logoY = (size - logoSize) / 2;

          // Add background for logo if specified
          if (options.logo?.backgroundColor && !options.style?.transparentBackground) {
            const borderRadius = options.logo?.borderRadius || 8;
            const padding = 4;

            ctx.fillStyle = options.logo.backgroundColor;
            ctx.beginPath();

            // Fallback for browsers without roundRect
            const x = logoX - padding;
            const y = logoY - padding;
            const width = logoSize + padding * 2;
            const height = logoSize + padding * 2;

            if (ctx.roundRect) {
              ctx.roundRect(x, y, width, height, borderRadius);
            } else {
              // Manual rounded rectangle
              ctx.moveTo(x + borderRadius, y);
              ctx.lineTo(x + width - borderRadius, y);
              ctx.quadraticCurveTo(x + width, y, x + width, y + borderRadius);
              ctx.lineTo(x + width, y + height - borderRadius);
              ctx.quadraticCurveTo(x + width, y + height, x + width - borderRadius, y + height);
              ctx.lineTo(x + borderRadius, y + height);
              ctx.quadraticCurveTo(x, y + height, x, y + height - borderRadius);
              ctx.lineTo(x, y + borderRadius);
              ctx.quadraticCurveTo(x, y, x + borderRadius, y);
            }
            ctx.fill();
          }

          // Draw logo
          ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);

          resolve(canvas.toDataURL());
        };
        const handleLogoError = (error: any) => {
          console.error('❌ Logo failed to load (attempt ' + (retryCount + 1) + '):', options.logo?.url, error);

          if (retryCount < maxRetries && options.logo?.url) {
            retryCount++;
            console.log('🔄 Retrying logo load...');

            // Try without crossOrigin on retry
            if (retryCount === 1) {
              logoImage.crossOrigin = '';
            }

            // Retry with a new timestamp - store URL safely
            const logoUrl = options.logo.url;
            const retryUrl = logoUrl + (logoUrl.includes('?') ? '&' : '?') + '_retry=' + retryCount + '_t=' + Date.now();
            logoImage.src = retryUrl;
          } else {
            console.warn('⚠️ Logo loading failed after ' + maxRetries + ' retries. Proceeding without logo.');
            // If logo fails after retries, return QR code without logo
            resolve(qrDataURL);
          }
        };

        logoImage.onerror = handleLogoError;
        logoImage.src = logoUrl;
      } else {
        resolve(canvas.toDataURL());
      }
    };
    qrImage.src = qrDataURL;
  });
};

export const generateQRCodeSVG = async (
  qrId: string,
  options: QRGenerationOptions = {}
): Promise<string> => {
  const url = generateQRCodeURL(qrId);

  const errorLevel = options.logo?.enabled ? 'H' : (options.errorCorrectionLevel || 'M');

  const qrOptions = {
    width: options.size || 256,
    margin: options.margin || 2,
    color: {
      dark: options.color?.dark || '#000000',
      light: options.style?.transparentBackground ? '#FFFFFF00' : (options.color?.light || '#FFFFFF'),
    },
    errorCorrectionLevel: errorLevel as any,
  };

  try {
    let svgString = await QRCode.toString(url, {
      type: 'svg',
      ...qrOptions
    });

    if (options.logo?.enabled && options.logo?.url) {
      svgString = await addLogoToSVG(svgString, options);
    }

    return svgString;
  } catch (error) {
    console.error('Error generating QR code SVG:', error);
    throw new Error('Failed to generate QR code SVG');
  }
};

const escapeSVGAttribute = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const imageURLToDataURL = async (url: string): Promise<string> => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error('Logo image could not be loaded');
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Logo image could not be embedded'));
    reader.readAsDataURL(blob);
  });
};

const addLogoToSVG = async (
  svgString: string,
  options: QRGenerationOptions
): Promise<string> => {
  if (!options.logo?.url) return svgString;

  const viewBoxMatch = svgString.match(/viewBox="[^"]*\s([\d.]+)\s([\d.]+)"/);
  const canvasSize = viewBoxMatch ? Number(viewBoxMatch[1]) : (options.size || 256);
  const logoSize = canvasSize * ((options.logo.size || 15) / 100);
  const logoX = (canvasSize - logoSize) / 2;
  const logoY = (canvasSize - logoSize) / 2;
  const padding = canvasSize * (4 / (options.size || 256));
  const borderRadius = canvasSize * ((options.logo.borderRadius || 0) / (options.size || 256));

  let embeddedLogoURL = options.logo.url;
  try {
    embeddedLogoURL = await imageURLToDataURL(options.logo.url);
  } catch (error) {
    // Keep the original URL as a fallback. Firebase Storage URLs are public,
    // while successful fetches produce a completely self-contained SVG.
    console.warn('Logo could not be embedded into SVG; using its public URL.', error);
  }

  const background = options.logo.backgroundColor && !options.style?.transparentBackground
    ? `<rect x="${logoX - padding}" y="${logoY - padding}" width="${logoSize + padding * 2}" height="${logoSize + padding * 2}" rx="${borderRadius}" fill="${escapeSVGAttribute(options.logo.backgroundColor)}"/>`
    : '';
  const logo = `<image href="${escapeSVGAttribute(embeddedLogoURL)}" x="${logoX}" y="${logoY}" width="${logoSize}" height="${logoSize}" preserveAspectRatio="xMidYMid meet"/>`;

  return svgString.replace('</svg>', `${background}${logo}</svg>`);
};

export const downloadQRCodeSVG = async (
  qrId: string,
  filename?: string,
  options: QRGenerationOptions = {}
): Promise<void> => {
  try {
    const svgString = await generateQRCodeSVG(qrId, options);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const objectURL = URL.createObjectURL(blob);
    triggerDownload(objectURL, filename || `qr-code-${qrId}.svg`);
    window.setTimeout(() => URL.revokeObjectURL(objectURL), 1000);
  } catch (error) {
    console.error('Error downloading QR code as SVG:', error);
    throw new Error('Failed to download QR code as SVG');
  }
};
